// Smart Sash Admin Panel - HTTP Interceptors
// Request and response interceptors for Axios

import { useUIStore } from '@/store/uiStore';
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { axiosClient } from './axiosClient';
import { handleApiError } from './errorHandler';

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
};

const REFRESH_ENDPOINT = '/Auth/refresh';
const PUBLIC_PATHS = ['/login', '/change-password', '/rfid-assignment', '/device-registration'];

// Shared in-flight refresh state — all concurrent 401s share one refresh attempt.
let isRefreshing = false;
let waitingQueue: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];

const isOnPublicPage = (): boolean => {
  if (typeof window === 'undefined') return false;
  return PUBLIC_PATHS.some((p) => window.location.pathname.includes(p));
};

const flushQueue = (error?: unknown): void => {
  if (error) {
    waitingQueue.forEach(({ reject }) => reject(error));
  } else {
    waitingQueue.forEach(({ resolve }) => resolve());
  }
  waitingQueue = [];
};

/**
 * Inspect a failed refresh response and decide what to do.
 *
 * Rules (from spec):
 *  - No response at all (network / timeout) → do NOT log out; show "no connection".
 *  - 401 + reason === "session_expired" → dispatch event; AuthContext clears state + redirects.
 *  - Anything else → do nothing (don't log out on transient errors).
 */
const handleRefreshFailure = (error: unknown): void => {
  const axiosErr = error as AxiosError;

  // A true network error: AxiosError with no server response at all (timeout, offline, etc.).
  // Do NOT log the user out — the session may still be alive; just notify.
  if (axiosErr.isAxiosError && !axiosErr.response) {
    useUIStore.getState().addNotification({
      type: 'warning',
      message: 'No internet connection. Please check your network.',
      duration: 5000,
    });
    return;
  }

  // Everything else means the server (or the envelope interceptor converting success:false)
  // rejected the refresh — the session is gone, redirect to login.
  window.dispatchEvent(new CustomEvent('auth:session-expired'));
};

/**
 * Request interceptor — stamps every request with a timestamp header.
 * No proactive token work: cookies are sent automatically by the browser.
 */
const requestInterceptor = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  config.headers['X-Request-Time'] = new Date().toISOString();

  if (import.meta.env.DEV) {
    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
  }

  return config;
};

const requestErrorInterceptor = (error: AxiosError): Promise<AxiosError> => {
  handleApiError(error, 'Request Error');
  return Promise.reject(error);
};

/**
 * Response success interceptor — logs in development.
 */
const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
  if (import.meta.env.DEV) {
    console.log(
      `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`
    );
  }

  return response;
};

/**
 * Response error interceptor — handles 401 with token refresh.
 *
 * On a 401:
 *  1. If a refresh is already in-flight, queue this request and wait.
 *  2. Otherwise start one refresh attempt (POST /Auth/refresh, no body — cookies do the work).
 *  3. On success: flush the queue and retry all waiting requests.
 *  4. On failure: flush the queue with the error and call handleRefreshFailure().
 */
const responseErrorInterceptor = async (error: AxiosError): Promise<unknown> => {
  const originalRequest = error.config as RetryableRequestConfig | undefined;

  if (
    error.response?.status === 401 &&
    originalRequest &&
    !originalRequest._retry &&
    !originalRequest._skipAuthRefresh &&
    !isOnPublicPage()
  ) {
    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        waitingQueue.push({ resolve, reject });
      })
        .then(() => axiosClient(originalRequest))
        .catch((err) => Promise.reject(err));
    }

    isRefreshing = true;
    originalRequest._retry = true;

    try {
      await axiosClient.post(
        REFRESH_ENDPOINT,
        {},
        { _skipAuthRefresh: true } as RetryableRequestConfig
      );
      flushQueue();
      return axiosClient(originalRequest);
    } catch (refreshError) {
      flushQueue(refreshError);
      handleRefreshFailure(refreshError);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }

  if (error.response?.status === 403) {
    console.error('[Auth] Forbidden - insufficient permissions');
  }

  if (error.response?.status && error.response.status >= 500) {
    console.error('[Server Error]', error.response.status);
  }

  handleApiError(error, 'Response Error');

  return Promise.reject(error);
};

/**
 * Setup all interceptors
 */
export const setupInterceptors = (): void => {
  axiosClient.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
  axiosClient.interceptors.response.use(responseInterceptor, responseErrorInterceptor);
};

/**
 * Clear all interceptors
 */
export const clearInterceptors = (): void => {
  axiosClient.interceptors.request.clear();
  axiosClient.interceptors.response.clear();
};
